<template>
  <span class="file-type-icon" :style="rootStyle" :title="ext || 'file'">
    <svg
      :width="dimension"
      :height="dimension"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M8.5 1 H21.6 L31 10 V28 Q31 31 28 31 H8.5 Q5.5 31 5.5 28 V4 Q5.5 1 8.5 1 Z"
        fill="#ffffff"
        stroke="#d1d5db"
        stroke-width="1.2"
        stroke-linejoin="round"
      />
      <path d="M21.6 1 V10 H31 Z" fill="#e5e7eb" />

      <template v-if="isImage">
        <g transform="translate(10 12.5) scale(1.15)">
          <path
            d="M1.38594 13.0333C0.998647 13.0333 0.670833 12.8992 0.4025 12.6308C0.134167 12.3625 0 12.0347 0 11.6474V1.38594C0 0.998647 0.134167 0.670833 0.4025 0.4025C0.670833 0.134167 0.998647 0 1.38594 0H11.6474C12.0347 0 12.3625 0.134167 12.6308 0.4025C12.8992 0.670833 13.0333 0.998647 13.0333 1.38594V11.6474C13.0333 12.0347 12.8992 12.3625 12.6308 12.6308C12.3625 12.8992 12.0347 13.0333 11.6474 13.0333H1.38594ZM1.38594 11.8833H11.6474C11.7064 11.8833 11.7605 11.8587 11.8095 11.8095C11.8587 11.7605 11.8833 11.7064 11.8833 11.6474V1.38594C11.8833 1.32691 11.8587 1.27286 11.8095 1.22379C11.7605 1.1746 11.7064 1.15 11.6474 1.15H1.38594C1.32691 1.15 1.27286 1.1746 1.22379 1.22379C1.1746 1.27286 1.15 1.32691 1.15 1.38594V11.6474C1.15 11.7064 1.1746 11.7605 1.22379 11.8095C1.27286 11.8587 1.32691 11.8833 1.38594 11.8833ZM2.49167 10.1583H10.6005L8.07952 6.79688L5.92691 9.59809L4.39358 7.63715L2.49167 10.1583ZM3.83333 4.79167C4.09873 4.79167 4.32477 4.69826 4.51145 4.51145C4.69826 4.32477 4.79167 4.09873 4.79167 3.83333C4.79167 3.56794 4.69826 3.3419 4.51145 3.15522C4.32477 2.96841 4.09873 2.875 3.83333 2.875C3.56794 2.875 3.3419 2.96841 3.15522 3.15522C2.96841 3.3419 2.875 3.56794 2.875 3.83333C2.875 4.09873 2.96841 4.32477 3.15522 4.51145C3.3419 4.69826 3.56794 4.79167 3.83333 4.79167Z"
            fill="#9CA3AF"
          />
        </g>
      </template>

      <template v-else>
        <rect
          x="1"
          y="16"
          :width="labelWidth"
          height="11"
          rx="2"
          fill="#ffffff"
          stroke="#d1d5db"
          stroke-width="1"
        />
        <text
          :x="labelCenterX"
          y="21.8"
          text-anchor="middle"
          dominant-baseline="central"
          :fill="color"
          font-family="'Open Sans', sans-serif"
          font-size="6.5"
          font-weight="700"
          letter-spacing="0.2"
        >
          {{ label }}
        </text>
      </template>
    </svg>
  </span>
</template>

<script>
const IMAGE_EXTENSIONS = [
  'png',
  'jpg',
  'jpeg',
  'gif',
  'svg',
  'webp',
  'bmp',
  'heic',
  'avif',
];

const COLOR_BY_EXTENSION = {
  pdf: '#ef4444',
  doc: '#2563eb',
  docx: '#2563eb',
  xls: '#2abb7f',
  xlsx: '#2abb7f',
  csv: '#2abb7f',
  ppt: '#f97316',
  pptx: '#f97316',
  txt: '#64748b',
  rtf: '#64748b',
  zip: '#8b5cf6',
  rar: '#8b5cf6',
  '7z': '#8b5cf6',
  gz: '#8b5cf6',
};

const DEFAULT_COLOR = '#6b7280';

export default {
  name: 'FileTypeIcon',
  props: {
    fileName: {
      type: String,
      default: '',
    },
    extension: {
      type: String,
      default: '',
    },
    size: {
      type: [Number, String],
      default: 32,
    },
  },
  computed: {
    ext() {
      if (this.extension) return this.extension.toLowerCase();
      const name = this.fileName || '';
      const dot = name.lastIndexOf('.');
      return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
    },
    isImage() {
      return IMAGE_EXTENSIONS.includes(this.ext);
    },
    label() {
      return this.ext ? this.ext.toUpperCase().slice(0, 4) : 'FILE';
    },
    color() {
      return COLOR_BY_EXTENSION[this.ext] || DEFAULT_COLOR;
    },
    labelWidth() {
      return Math.round((this.label.length * 4.6 + 7) * 10) / 10;
    },
    labelCenterX() {
      return Math.round((1 + this.labelWidth / 2) * 10) / 10;
    },
    dimension() {
      return /^\d+(\.\d+)?$/.test(String(this.size))
        ? `${this.size}px`
        : this.size;
    },
    rootStyle() {
      return {
        width: this.dimension,
        height: this.dimension,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.file-type-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  flex-shrink: 0;
  vertical-align: middle;

  svg {
    display: block;
  }
}
</style>
